import React, { useEffect, useState, useMemo } from 'react';
import classnames from 'classnames';
import { Container } from 'react-bootstrap';

import DangerouslySetInnerHtml from 'components/common/DangerouslySetInnerHtml';
import IconCustom from 'components/common/IconCustom';
import useMatchMedia from 'hooks/useMatchMedia';
import { TABLET_BREAKPOINT } from 'utils/constants';

import { getFIltersData, hasSelectedFilters, getLocationInfo, updateUrlParams } from './utils';
import { ProductFiltersComponent } from './model';

import './ProductFilters.scss';

const ProductFilters: ProductFiltersComponent = ({
  productFilters: [
    {
      properties: { intro, title, filters, selectedFilterTextMarker, clearFiltersLabel },
    },
  ],
  products,
  setProducts,
}) => {
  const { initialState, validTypes, validTypeValues } = useMemo(() => getFIltersData(filters), []);
  const [selectedFilters, setSelectedFilters] = useState(initialState);
  const [activeFilterType, setActiveFilterType] = useState<string | null>(null);

  const isDesktop = useMatchMedia(`(min-width: ${TABLET_BREAKPOINT}px)`);

  useEffect(() => {
    const appliedFilters = {
      ...initialState,
    };

    const { searchParams } = getLocationInfo();
    if (!searchParams.size) return;
    Object.entries(validTypeValues).forEach(([filterType, filterValues]) => {
      const urlValue = searchParams.get(filterType);
      if (!urlValue || !filterValues.includes(urlValue)) return;
      appliedFilters[filterType] = urlValue;
    });

    if (hasSelectedFilters(appliedFilters)) setSelectedFilters(appliedFilters);
  }, []);

  useEffect(() => {
    setProducts(() => ({
      nodes: products.nodes.filter((product) =>
        Object.entries(selectedFilters).every(([filterType, filterValue]) => {
          if (!filterValue) return true;
          return product.tags.some(
            ({ isFilterTag, type, value }) =>
              isFilterTag && type === filterType && value === filterValue
          );
        })
      ),
    }));
  }, [selectedFilters, products]);

  const dropdownStateChangeHandler = (type: string): void => {
    setActiveFilterType((prevActiveType) => (prevActiveType === type ? null : type));
  };

  const filterChangeHandler = (type: string, value: string): void => {
    const theSameValue = selectedFilters[type] === value;

    setSelectedFilters((prevSelected) => ({
      ...prevSelected,
      [type]: theSameValue ? null : value,
    }));

    const { pathname, searchParams } = getLocationInfo();
    if (theSameValue) searchParams.delete(type);
    else searchParams.set(type, value);
    updateUrlParams(pathname, searchParams);
  };

  const resetFiltersHandler = (): void => {
    setActiveFilterType(null);
    setSelectedFilters(initialState);

    const { pathname, searchParams } = getLocationInfo();
    validTypes.forEach((filterType) => searchParams.delete(filterType));
    updateUrlParams(pathname, searchParams);
  };

  return (
    <section className="product-filters">
      <Container fluid>
        <DangerouslySetInnerHtml className="product-filters__intro" html={intro} />
        <DangerouslySetInnerHtml className="product-filters__title" html={title} />
        <div className="product-filters__dropdowns-container">
          {filters.map(({ properties: { label, type, options } }) => {
            const valueByType = selectedFilters[type];
            const isFilterTypeActive = activeFilterType === type;

            return (
              <div
                key={type}
                className={classnames('product-filters__dropdown', {
                  'product-filters__dropdown--active': isFilterTypeActive,
                })}
              >
                <button
                  type="button"
                  className="product-filters__dropdown-toggler"
                  onClick={() => dropdownStateChangeHandler(type)}
                >
                  <div>
                    <span className="product-filters__dropdown-title">{label}</span>
                    {valueByType ? (
                      <span className="product-filters__dropdown-counter">
                        {selectedFilterTextMarker}
                      </span>
                    ) : null}
                  </div>
                  <IconCustom icon="chevron-down" />
                </button>
                {!isDesktop && isFilterTypeActive ? (
                  <ProductFilters.Options
                    type={type}
                    options={options}
                    valueByType={valueByType}
                    filterChangeHandler={filterChangeHandler}
                  />
                ) : null}
              </div>
            );
          })}
          {hasSelectedFilters(selectedFilters) ? (
            <button
              type="button"
              className="product-filters__clear-filters"
              data-clear="true"
              onClick={resetFiltersHandler}
            >
              <IconCustom icon="close" />
              <span>{clearFiltersLabel}</span>
            </button>
          ) : null}
        </div>
        {isDesktop && activeFilterType ? (
          <>
            {filters.map(({ properties: { type, options } }) => {
              if (activeFilterType !== type) return null;

              return (
                <ProductFilters.Options
                  key={type}
                  type={type}
                  options={options}
                  valueByType={selectedFilters[type]}
                  filterChangeHandler={filterChangeHandler}
                />
              );
            })}
          </>
        ) : null}
      </Container>
    </section>
  );
};

ProductFilters.Options = ({ type, options, valueByType, filterChangeHandler }) => (
  <ul className="product-filters__dropdown-options">
    {options.map(({ properties: { label, value } }) => (
      <li key={value}>
        <button
          type="button"
          className={classnames('product-filters__dropdown-option', {
            'product-filters__dropdown-option--active': valueByType === value,
          })}
          onClick={() => filterChangeHandler(type, value)}
        >
          <span>{label}</span>
          {valueByType === value ? <IconCustom icon="close" /> : null}
        </button>
      </li>
    ))}
  </ul>
);

export default ProductFilters;
